"use client";

import { useEffect, useState } from "react";

export type AbVariant = "A" | "B";

/**
 * Reads the bucket `proxy.ts` assigned to this visitor, outside React.
 *
 * Use this inside an effect that reports a view. The hook below cannot be used
 * there: its value changes from "A" to the real bucket one tick after mount,
 * which would re-run the effect and report the same view twice — once under
 * each variant, wrecking the very numbers the experiment is measuring.
 */
export function readAbVariant(cookieName: string): AbVariant {
	if (typeof document === "undefined") {
		return "A";
	}
	const match = document.cookie.match(
		new RegExp(`(?:^|;\\s*)${cookieName}=([^;]*)`),
	);
	return match?.[1] === "B" ? "B" : "A";
}

/**
 * The visitor's bucket, for choosing what to render.
 *
 * The first render always returns "A" and the real value arrives in an effect.
 * That is deliberate: reading `document.cookie` during render would make the
 * server and client markup disagree and blow up hydration. "A" is the control
 * copy, so a visitor whose JS is slow sees exactly what ships today.
 */
export function useAbVariant(cookieName: string): AbVariant {
	const [variant, setVariant] = useState<AbVariant>("A");

	useEffect(() => {
		setVariant(readAbVariant(cookieName));
	}, [cookieName]);

	return variant;
}
